import { Injectable } from '@angular/core';
import { CoolHttp } from '@angular-cool/http';
import {
  CampaignSessionSectionDTO,
  CampaignSessionSectionEntityFullDTO,
  CampaignSessionSectionEntityId,
  CampaignSessionSectionEntityMinimumDTO,
  CampaignSessionSectionIdentifiers,
  SessionVisibility
} from '../../../../../common/dto/campaigns.dto';
import { ServerUrls } from '../../../../../common/dto/server-urls';
import { MINUTE_IN_MILLISECONDS } from '../../../../../common/utils/date.utils';
import { Observable, Subject } from 'rxjs';
import { CacheState, CacheStateUpdater } from '@coool/cachestate';
import { Position } from '../../../../../common/dto/common.dto';
import { map } from 'rxjs/operators';

const campaignSessionSectionEntitiesCacheUpdater = new Subject<string | void>();

@Injectable()
export class CampaignSessionSectionEntitiesService {
  constructor(private _http: CoolHttp) {
  }

  @CacheState({
    maxAgeMS: 5 * MINUTE_IN_MILLISECONDS,
    cacheKey: {
      generator: args => `campaign-session-section-entities_${ args[0].campaignSessionSectionId }`
    },
    updatedObservable: campaignSessionSectionEntitiesCacheUpdater
      .pipe(map(key => key ? (key.match(/^([^_]*_[^_]*).*$/)?.[0] ?? key) : undefined)),
    updateOnlySpecific: true
  })
  public getCampaignSessionSectionEntitiesCACHED(
    sectionIdentifiers: CampaignSessionSectionIdentifiers
  ): Observable<CampaignSessionSectionEntityMinimumDTO[]> {
    return this._http.getObservable(
      ServerUrls.Campaigns.children.Sessions.children.Sections.children.Entities.children.Get.getAbsoluteUrlWithParams({
        [ServerUrls.Campaigns.children.Sessions.params.campaignId]: sectionIdentifiers.campaignId,
        [ServerUrls.Campaigns.children.Sessions.children.Sections.params.campaignSessionId]: sectionIdentifiers.campaignSessionId,
        [ServerUrls.Campaigns.children.Sessions.children.Sections.children.Entities.params.campaignSessionSectionId]:
        sectionIdentifiers.campaignSessionSectionId
      })
    );
  }

  @CacheState({
    maxAgeMS: 5 * MINUTE_IN_MILLISECONDS,
    cacheKey: {
      generator: args => `campaign-session-section-entities_${ args[0].campaignSessionSectionId }_${ args[1] }`
    },
    updatedObservable: campaignSessionSectionEntitiesCacheUpdater.pipe(map(_ => undefined)),
    updateOnlySpecific: true
  })
  public getCampaignSessionSectionEntityByIdCACHED(
    sectionIdentifiers: CampaignSessionSectionIdentifiers,
    entityId: CampaignSessionSectionEntityId
  ): Observable<CampaignSessionSectionEntityFullDTO> {
    return this._http.getObservable(
      ServerUrls.Campaigns.children.Sessions.children.Sections.children.Entities.children.GetById.getAbsoluteUrlWithParams({
        [ServerUrls.Campaigns.children.Sessions.params.campaignId]: sectionIdentifiers.campaignId,
        [ServerUrls.Campaigns.children.Sessions.children.Sections.params.campaignSessionId]: sectionIdentifiers.campaignSessionId,
        [ServerUrls.Campaigns.children.Sessions.children.Sections.children.Entities.params.campaignSessionSectionId]:
        sectionIdentifiers.campaignSessionSectionId,
        [ServerUrls.Campaigns.children.Sessions.children.Sections.children.Entities.children.GetById.params.campaignSessionSectionEntityId]:
        entityId
      })
    );
  }

  @CacheStateUpdater({
    cacheKeyGenerator: args => `campaign-session-section-entities_${ args[0].campaignSessionSectionId }_${ args[1] }`,
    updatedNotifier: campaignSessionSectionEntitiesCacheUpdater
  })
  public async updateCampaignSessionSectionEntityPositionAsync(
    sectionIdentifiers: CampaignSessionSectionIdentifiers,
    sectionEntityId: CampaignSessionSectionEntityId,
    position: Position
  ): Promise<CampaignSessionSectionDTO> {
    return await this._http.postAsync(
      ServerUrls.Campaigns.children.Sessions.children.Sections.children.Entities.children.UpdatePosition.getAbsoluteUrlWithParams({
        [ServerUrls.Campaigns.children.Sessions.params.campaignId]: sectionIdentifiers.campaignId,
        [ServerUrls.Campaigns.children.Sessions.children.Sections.params.campaignSessionId]: sectionIdentifiers.campaignSessionId,
        [ServerUrls.Campaigns.children.Sessions.children.Sections.children.Entities.params.campaignSessionSectionId]:
        sectionIdentifiers.campaignSessionSectionId,
        [ServerUrls.Campaigns.children.Sessions.children.Sections.children.Entities.children.UpdatePosition.params
          .campaignSessionSectionEntityId]: sectionEntityId
      }),
      position
    );
  }

  @CacheStateUpdater({
    cacheKeyGenerator: args => `campaign-session-section-entities_${ args[0].campaignSessionSectionId }_${ args[1] }`,
    updatedNotifier: campaignSessionSectionEntitiesCacheUpdater
  })
  public async updateCampaignSessionSectionEntityVisibilityAsync(
    sectionIdentifiers: CampaignSessionSectionIdentifiers,
    sectionEntityId: CampaignSessionSectionEntityId,
    visibility: SessionVisibility
  ): Promise<CampaignSessionSectionDTO> {
    return await this._http.postAsync(
      ServerUrls.Campaigns.children.Sessions.children.Sections.children.Entities.children.UpdateVisibility.getAbsoluteUrlWithParams({
        [ServerUrls.Campaigns.children.Sessions.params.campaignId]: sectionIdentifiers.campaignId,
        [ServerUrls.Campaigns.children.Sessions.children.Sections.params.campaignSessionId]: sectionIdentifiers.campaignSessionId,
        [ServerUrls.Campaigns.children.Sessions.children.Sections.children.Entities.params.campaignSessionSectionId]:
        sectionIdentifiers.campaignSessionSectionId,
        [ServerUrls.Campaigns.children.Sessions.children.Sections.children.Entities.children.UpdateVisibility.params
          .campaignSessionSectionEntityId]: sectionEntityId
      }),
      {
        visibility: visibility
      }
    );
  }

  @CacheStateUpdater({
    cacheKeyGenerator: args => `campaign-session-section-entities_${ args[0].campaignSessionSectionId }${ args[1].id ? `_${ args[1].id }` : '' }`,
    updatedNotifier: campaignSessionSectionEntitiesCacheUpdater
  })
  public async upsertCampaignSessionSectionEntityAsync(
    sectionIdentifiers: CampaignSessionSectionIdentifiers,
    sectionEntity: CampaignSessionSectionEntityFullDTO
  ): Promise<CampaignSessionSectionEntityFullDTO> {
    return await this._http.postAsync(
      ServerUrls.Campaigns.children.Sessions.children.Sections.children.Entities.children.Upsert.getAbsoluteUrlWithParams({
        [ServerUrls.Campaigns.children.Sessions.params.campaignId]: sectionIdentifiers.campaignId,
        [ServerUrls.Campaigns.children.Sessions.children.Sections.params.campaignSessionId]: sectionIdentifiers.campaignSessionId,
        [ServerUrls.Campaigns.children.Sessions.children.Sections.children.Entities.params.campaignSessionSectionId]:
        sectionIdentifiers.campaignSessionSectionId
      }),
      sectionEntity
    );
  }

  @CacheStateUpdater({
    cacheKeyGenerator: args => `campaign-session-section-entities_${ args[0].campaignSessionSectionId }`,
    updatedNotifier: campaignSessionSectionEntitiesCacheUpdater
  })
  public async removeCampaignSessionSectionEntityAsync(
    sectionIdentifiers: CampaignSessionSectionIdentifiers,
    sectionEntityId: CampaignSessionSectionEntityId
  ) {
    return await this._http.deleteAsync(
      ServerUrls.Campaigns.children.Sessions.children.Sections.children.Entities.children.Delete.getAbsoluteUrlWithParams({
        [ServerUrls.Campaigns.children.Sessions.params.campaignId]: sectionIdentifiers.campaignId,
        [ServerUrls.Campaigns.children.Sessions.children.Sections.params.campaignSessionId]: sectionIdentifiers.campaignSessionId,
        [ServerUrls.Campaigns.children.Sessions.children.Sections.children.Entities.params.campaignSessionSectionId]:
        sectionIdentifiers.campaignSessionSectionId,
        [ServerUrls.Campaigns.children.Sessions.children.Sections.children.Entities.children.Delete.params.campaignSessionSectionEntityId]:
        sectionEntityId
      })
    );
  }
}
